import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import ReactHtmlParser from "react-html-parser";
import QuizProgressBar from "../QuizProgressBar";

const QuizContainer = styled.div`
  min-height: 100vh;
  font-family: "Quicksand", sans-serif;
  background: ${(props) =>
    props.isDarkMode ? "var(--background-dark)" : "var(--background-light)"};
  transition: background 0.5s ease;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 20px 0;
  background: ${(props) =>
    props.isDarkMode ? "var(--background-dark)" : "var(--background-light)"};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 600px;
  margin: 0 auto;
`;

const TimerBadge = styled.div`
  background: #f3eefa;
  border-radius: 12px;
  padding: 6px 14px;
  font-size: 1rem;
  font-weight: 700;
  color: #8747d4;
  font-family: "Quicksand", sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ProgressBarWrapper = styled.div`
  flex-grow: 1;
`;

const QuestionCounter = styled.div`
  font-size: 0.85rem;
  color: #888;
  font-weight: 600;
  text-align: center;
  max-width: 600px;
  margin: 8px auto 0;
  padding: 0 20px;
`;

const ContentArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 10px 20px 0;
`;

const AnimatedSlide = styled(motion.div)`
  width: 100%;
`;

const QuestionText = styled.div`
  font-size: 2.5vh;
  font-weight: bold;
  text-align: center;
  color: #a678de;
  padding: 20px;
  margin: 30px;
  line-height: 1.5;
`;

const AnswerOption = styled(motion.div)`
  font-size: 2.5vh;
  font-weight: bold;
  text-align: center;
  font-family: "Quicksand", sans-serif;
  display: flex;
  justify-content: center;
  padding: 14px 20px;
  margin: 20px;
  border-radius: 10px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  background: ${(props) => {
    if (!props.selected) return "#D2CFE6";
    if (props.showResult) return props.isCorrect ? "#DAF7A6" : "#ff726f";
    return "#b8b8b8";
  }};
  transition: background 0.3s;
`;

const ExplanationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 20px;
  margin: 0 20px;
  font-size: 0.9rem;
`;

const FixedButton = styled.button`
  font-family: "Quicksand", sans-serif;
  padding: 1rem 2rem;
  font-size: 1rem;
  width: 360px;
  max-width: 95%;
  border: 0;
  border-radius: 16px;
  font-weight: bold;
  color: white;
  cursor: pointer;
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 4px 0 ${(props) => props.shadowColor || "#6535a3"};
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.01s ease;

  &:hover {
    filter: brightness(110%);
  }

  &:active {
    transform: translateX(-50%) translateY(4px);
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 90%;
    font-size: 1.2rem;
    padding: 0.8rem 1.6rem;
  }
`;

function renderTextWithArabic(text) {
  if (!text) return null;
  return text.split(/(<[^>]*>|[^<]+)/).map((segment, i) => {
    if (segment.startsWith("<")) return <span key={i}>{ReactHtmlParser(segment)}</span>;
    return segment.split(/(\s+)/).map((word, j) =>
      /[\u0600-\u06FF]/.test(word) ? (
        <span key={`${i}-${j}`} style={{ fontSize: "5vh" }}>{word}</span>
      ) : (
        word
      )
    );
  });
}

function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function KnowledgeChallengeQuiz({
  questions,
  currentIndex,
  timer,
  onAnswer,
  onContinue,
  answered,
  selectedAnswer,
  isDarkMode,
}) {
  const currentQuestion = questions[currentIndex];
  const progressPercentage = Math.ceil(((currentIndex + (answered ? 1 : 0)) / questions.length) * 100);

  const shuffleArray = useCallback((array) => {
    let shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const [shuffledAnswers, setShuffledAnswers] = useState([]);
  useEffect(() => {
    if (currentQuestion && currentQuestion.answers) {
      setShuffledAnswers(shuffleArray(currentQuestion.answers));
    }
  }, [currentIndex, currentQuestion, shuffleArray]);

  if (!currentQuestion) return null;

  const isCorrectAnswer = selectedAnswer && selectedAnswer.isCorrect;

  return (
    <QuizContainer isDarkMode={isDarkMode}>
      <StickyHeader isDarkMode={isDarkMode}>
        <HeaderRow>
          <TimerBadge>⏱ {formatTime(timer)}</TimerBadge>
          <ProgressBarWrapper>
            <QuizProgressBar percentage={progressPercentage} />
          </ProgressBarWrapper>
        </HeaderRow>
        <QuestionCounter>
          Question {currentIndex + 1} of {questions.length}
        </QuestionCounter>
      </StickyHeader>

      <ContentArea>
        <AnimatePresence mode="wait">
          <AnimatedSlide
            key={currentIndex}
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "-100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              position: "absolute",
              width: "100%",
              left: 0,
              right: 0,
            }}
          >
            <QuestionText>
              {renderTextWithArabic(currentQuestion.question)}
            </QuestionText>
            {shuffledAnswers.map((answerItem, idx) => {
              const isSelected = selectedAnswer && selectedAnswer.answer === answerItem.answer;
              return (
                <div key={idx}>
                  <AnswerOption
                    selected={isSelected}
                    showResult={answered && isSelected}
                    isCorrect={answerItem.isCorrect}
                    disabled={answered}
                    whileTap={!answered ? { scale: 0.8 } : {}}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    onClick={() => onAnswer(answerItem)}
                  >
                    <div>{renderTextWithArabic(answerItem.answer)}</div>
                  </AnswerOption>
                  {answered && isSelected && (
                    <ExplanationRow>
                      {answerItem.isCorrect ? (
                        <>
                          <i className="correctCheckMark fa-solid fa-check" />
                          <span style={{ color: "#4caf50", fontWeight: 600 }}>Correct!</span>
                        </>
                      ) : (
                        <>
                          <i className="wrongCross fa-solid fa-xmark" />
                          <span style={{ color: "#e53935" }}>
                            {renderTextWithArabic(answerItem.explanation)}
                          </span>
                        </>
                      )}
                    </ExplanationRow>
                  )}
                </div>
              );
            })}
          </AnimatedSlide>
        </AnimatePresence>
        <div style={{ minHeight: "800px" }} />
      </ContentArea>
      <div style={{ height: "500px" }} />

      {answered && (
        <FixedButton
          onClick={onContinue}
          style={{
            backgroundColor:
              currentIndex >= questions.length - 1
                ? "#8747d4"
                : isCorrectAnswer
                ? "rgb(80, 200, 120)"
                : "red",
          }}
          shadowColor={
            currentIndex >= questions.length - 1
              ? "#6535a3"
              : isCorrectAnswer
              ? "rgb(60, 150, 90)"
              : "#cc0000"
          }
        >
          {currentIndex < questions.length - 1 ? "CONTINUE" : "FINISH"}
        </FixedButton>
      )}
    </QuizContainer>
  );
}

export default KnowledgeChallengeQuiz;
